import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";

/**
 * FUNIL DE ENTRADA.
 *
 * ⚠️ A REGRA MORA NA TELA QUE É PULADA: "Alfabetização: se idade < 5 → pula para
 * Religião". A condição é avaliada AO CHEGAR na tela, não ao sair.
 *
 * O simulador abaixo REPLICA o navigate.ts do backend (que é o mesmo algoritmo que o app
 * implementa). Se os três divergirem, o painel mente para o admin — ele configura uma
 * coisa e a criança vê outra.
 */

const rotulo = (lista, key) => lista.find((x) => x.key === key)?.label || key;

/** Espelho de navigate.ts — MESMA semântica, mesma ordem de avaliação. */
function avaliar(estado, r) {
  const v = estado[r.field];
  if (v === undefined || v === null) return false;
  switch (r.operator) {
    case "eq": return String(v) === r.value;
    case "neq": return String(v) !== r.value;
    case "lt": return Number(v) < Number(r.value);
    case "lte": return Number(v) <= Number(r.value);
    case "gt": return Number(v) > Number(r.value);
    case "gte": return Number(v) >= Number(r.value);
    case "contains":
      return Array.isArray(v) ? v.map(String).includes(r.value) : String(v).includes(r.value);
    case "in":
      return r.value.split(",").map((s) => s.trim()).includes(String(v));
    default: return false;
  }
}

function caminhoDo(telas, estado) {
  const ord = telas.filter((t) => t.visible).sort((a, b) => a.order - b.order);
  const porId = new Map(ord.map((t) => [t.id, t]));
  const indicePorId = new Map(ord.map((t, i) => [t.id, i]));

  // resolve o que REALMENTE aparece, a partir de uma candidata
  const resolver = (tela, visitadas) => {
    if (!tela) return null;
    if (visitadas.has(tela.id)) return null; // trava anti-loop
    visitadas.add(tela.id);

    const regras = (tela.regras || [])
      .filter((r) => r.enabled)
      .sort((a, b) => a.priority - b.priority);

    for (const r of regras) {
      if (avaliar(estado, r)) return resolver(porId.get(r.targetScreenId), visitadas);
    }
    return tela;
  };

  const caminho = [];
  const jaNoCaminho = new Set();
  let atual = resolver(ord[0], new Set());

  while (atual && caminho.length < 50) {
    if (jaNoCaminho.has(atual.id)) break;
    caminho.push(atual);
    jaNoCaminho.add(atual.id);

    const i = indicePorId.get(atual.id);
    atual = resolver(ord[i + 1], new Set());
  }
  return caminho;
}

export default function Funnel() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [funil, setFunil] = useState(null);
  const [aba, setAba] = useState("fluxo");
  const [novaRegra, setNovaRegra] = useState(null);
  const [idade, setIdade] = useState(4);

  const carregar = useCallback(
    () => rodar(async () => setDados(await apiGet(API_ROUTES.ADMIN.quizScreens()))),
    [rodar]
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (aba === "evasao" && !funil)
      rodar(async () => setFunil(await apiGet(API_ROUTES.ADMIN.quizFunnel(30))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  // Memoizados: `dados?.telas || []` cria um array novo a cada render, e isso faria os
  // useMemo abaixo recalcularem sempre (e o React reclamar, com razão).
  const telas = useMemo(() => dados?.telas || [], [dados]);
  const vocab = useMemo(
    () => dados?.vocabulario || { campos: [], operadores: [] },
    [dados]
  );
  const visiveis = useMemo(() => telas.filter((t) => t.visible), [telas]);

  const caminho = useMemo(
    () => caminhoDo(telas, { idade_filho: Number(idade) }),
    [telas, idade]
  );
  const puladas = visiveis.filter((t) => !caminho.some((c) => c.id === t.id));

  const acao = (fn, msg) =>
    rodar(async () => {
      await fn();
      await carregar();
    }, msg);

  const mover = (i, d) => {
    const nova = [...telas];
    const j = i + d;
    if (j < 0 || j >= nova.length) return;
    [nova[i], nova[j]] = [nova[j], nova[i]];
    acao(() =>
      apiSend(API_ROUTES.ADMIN.quizReorder(), "POST", { ids: nova.map((t) => t.id) })
    );
  };

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Funil de entrada"
        subtitulo="Monte o quiz, veja o caminho de cada criança e onde as pessoas desistem."
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid var(--border)" }}>
          {[["fluxo", "Fluxo"], ["evasao", "Onde desistem"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setAba(k)}
              style={{
                background: "none",
                border: "none",
                borderBottom: `2px solid ${aba === k ? "var(--red)" : "transparent"}`,
                color: aba === k ? "var(--text)" : "var(--text2)",
                fontWeight: aba === k ? 600 : 500,
                fontSize: 13.5,
                padding: "10px 14px",
                cursor: "pointer",
                marginBottom: -1,
                font: "inherit",
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {aba === "fluxo" && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 22, alignItems: "start" }}>
            {/* ---------- telas ---------- */}
            <section>
              <p style={{ fontSize: 13, color: "var(--text2)", margin: "0 0 12px" }}>
                {visiveis.length} de {telas.length} telas visíveis
              </p>

              {telas.length === 0 && (
                <div className="adm__card">
                  <p className="adm__vazio">
                    Nenhuma tela registrada. As telas são <strong>componentes do app</strong> —
                    a equipe de desenvolvimento as registra aqui, e você decide quais aparecem.
                  </p>
                </div>
              )}

              {telas.map((t, i) => (
                <div
                  key={t.id}
                  className="adm__card"
                  style={{
                    padding: 15,
                    marginBottom: 10,
                    display: "flex",
                    gap: 13,
                    opacity: t.visible ? 1 : 0.5,
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 26 }}>
                    <button className="adm__btnGhost" style={{ padding: "2px 6px", fontSize: 9 }} disabled={i === 0} onClick={() => mover(i, -1)}>▲</button>
                    <span style={{ fontSize: 12, color: "var(--text3)" }}>{t.order}</span>
                    <button className="adm__btnGhost" style={{ padding: "2px 6px", fontSize: 9 }} disabled={i === telas.length - 1} onClick={() => mover(i, 1)}>▼</button>
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <strong style={{ fontSize: 14.5 }}>{t.name}</strong>
                      <code className="adm__code">{t.key}</code>
                      {!t.visible && <span className="adm__chip adm__chip--orange">oculta</span>}
                      {t.skippable && (
                        <span
                          className="adm__chip"
                          title="O usuário pode pular esta pergunta — aparece um 'Pular' no app"
                          style={{ background: "var(--blueSoft)", color: "var(--blue)" }}
                        >
                          pode pular
                        </span>
                      )}
                      {t.minAppVersion && (
                        <span className="adm__chip" title="Apps mais antigos pulam esta tela">
                          app ≥ {t.minAppVersion}
                        </span>
                      )}
                    </div>

                    {t.description && (
                      <p style={{ margin: "5px 0 0", fontSize: 12.5, color: "var(--text2)" }}>{t.description}</p>
                    )}
                    <p style={{ margin: "5px 0 0", fontSize: 11.5, color: "var(--text3)" }}>
                      {t.fields?.length
                        ? <>Coleta: <code className="adm__code">{t.fields.join(", ")}</code></>
                        : "Não coleta nenhum campo"}
                    </p>

                    {(t.regras || []).map((r) => (
                      <div
                        key={r.id}
                        style={{
                          marginTop: 9,
                          background: "var(--blueSoft)",
                          borderLeft: "3px solid var(--blue)",
                          borderRadius: 8,
                          padding: "9px 12px",
                          fontSize: 12.5,
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          alignItems: "center",
                          opacity: r.enabled ? 1 : 0.45,
                        }}
                      >
                        <span>
                          <strong>Esta tela é pulada</strong> se{" "}
                          <strong>{rotulo(vocab.campos, r.field)}</strong>{" "}
                          {rotulo(vocab.operadores, r.operator)} <strong>{r.value}</strong> → vai
                          para <strong>{r.destinoNome}</strong>
                        </span>
                        <span style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                          <button
                            className="adm__btnGhost"
                            style={{ padding: "3px 8px", fontSize: 11 }}
                            onClick={() =>
                              acao(() =>
                                apiSend(API_ROUTES.ADMIN.quizRule(r.id), "PATCH", { enabled: !r.enabled })
                              )
                            }
                          >
                            {r.enabled ? "Pausar" : "Ativar"}
                          </button>
                          <button
                            className="adm__btnGhost"
                            style={{ padding: "3px 8px", fontSize: 11 }}
                            onClick={() =>
                              acao(
                                () => apiSend(API_ROUTES.ADMIN.quizRule(r.id), "DELETE"),
                                "Desvio removido."
                              )
                            }
                          >
                            Remover
                          </button>
                        </span>
                      </div>
                    ))}

                    <div style={{ marginTop: 11, display: "flex", gap: 7 }}>
                      <button
                        className="adm__btnGhost"
                        onClick={() =>
                          acao(
                            () =>
                              apiSend(API_ROUTES.ADMIN.quizScreen(t.id), "PATCH", {
                                visible: !t.visible,
                              }),
                            t.visible ? "Tela ocultada." : "Tela exibida."
                          )
                        }
                      >
                        {t.visible ? "Ocultar" : "Exibir"}
                      </button>
                      <button className="adm__btnGhost" onClick={() => setNovaRegra(t)}>
                        + desvio
                      </button>
                      {t.fields?.length > 0 && (
                        <button
                          className="adm__btnGhost"
                          title="Deixa o usuário responder só se quiser. Bom para pergunta sensível — ex.: religião. Diferente do desvio: aqui quem decide é o usuário, não uma regra."
                          onClick={() =>
                            acao(
                              () =>
                                apiSend(API_ROUTES.ADMIN.quizScreen(t.id), "PATCH", {
                                  skippable: !t.skippable,
                                }),
                              t.skippable ? "Agora é obrigatória." : "Agora pode ser pulada."
                            )
                          }
                        >
                          {t.skippable ? "Tornar obrigatória" : "Permitir pular"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </section>

            {/* ---------- o simulador ---------- */}
            <aside className="adm__card" style={{ padding: 18, position: "sticky", top: 100 }}>
              <strong style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 7 }}>
                👁 Como fica na prática
              </strong>
              <p style={{ fontSize: 12.5, color: "var(--text2)", margin: "7px 0 16px", lineHeight: 1.55 }}>
                Escolha a idade e veja o caminho que a criança vai percorrer. Ajuste antes de
                publicar.
              </p>

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 5 }}>
                <span style={{ color: "var(--text2)" }}>Idade do filho</span>
                <span>
                  <strong style={{ color: "var(--red)", fontSize: 15 }}>{idade}</strong>{" "}
                  <span style={{ color: "var(--text3)" }}>anos</span>
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="14"
                value={idade}
                onChange={(e) => setIdade(e.target.value)}
                style={{ width: "100%", accentColor: "var(--red)", marginBottom: 16 }}
              />

              <div style={{ fontSize: 10.5, letterSpacing: 1, color: "var(--text3)", fontWeight: 600, marginBottom: 9 }}>
                CAMINHO ({caminho.length} TELAS)
              </div>

              {caminho.map((t, i) => (
                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0" }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      background: "var(--red)",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ fontSize: 13.5 }}>{t.name}</span>
                </div>
              ))}

              {puladas.length > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    background: "rgba(255,140,0,.10)",
                    border: "1px solid rgba(255,140,0,.3)",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 12,
                    color: "var(--orange)",
                    lineHeight: 1.5,
                  }}
                >
                  ✕ <strong>Puladas nesta idade:</strong>{" "}
                  {puladas.map((t) => t.name).join(", ")}
                </div>
              )}
            </aside>
          </div>
        )}

        {aba === "evasao" && <Evasao funil={funil} />}
      </div>

      {novaRegra && dados && (
        <ModalDesvio
          origem={novaRegra}
          telas={telas}
          vocab={vocab}
          onFechar={() => setNovaRegra(null)}
          onSalvar={(r) =>
            acao(async () => {
              await apiSend(API_ROUTES.ADMIN.quizRules(), "POST", r);
              setNovaRegra(null);
            }, "Desvio criado.")
          }
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

function Evasao({ funil }) {
  if (!funil) return <p className="adm__vazio">Carregando…</p>;

  return (
    <>
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          ["Iniciaram", funil.iniciaram, null],
          ["Concluíram", funil.concluiram, null],
          ["Conversão", funil.conversao, "var(--red)"],
        ].map(([l, v, cor]) => (
          <div key={l} className="adm__card" style={{ padding: "16px 22px", minWidth: 140 }}>
            <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 5 }}>{l}</div>
            <strong style={{ fontSize: 27, color: cor || "var(--text)" }}>{v}</strong>
          </div>
        ))}
      </div>

      {funil.piorTela && (
        <div
          style={{
            background: "var(--redSoft)",
            border: "1px solid rgba(229,9,20,.3)",
            borderRadius: 12,
            padding: "13px 16px",
            fontSize: 13.5,
            marginBottom: 18,
            lineHeight: 1.6,
          }}
        >
          🩸 <strong>Maior sangria: {funil.piorTela.nome}</strong> — perde{" "}
          <strong>{funil.piorTela.evasaoAqui}</strong> de quem chega ali. É por onde começar a
          otimizar.
        </div>
      )}

      {funil.iniciaram === 0 && (
        <p className="adm__vazio">
          Ainda não há dados. Os números aparecem quando o app começar a enviar os eventos do
          funil.
        </p>
      )}

      <div className="adm__card" style={{ overflow: "hidden" }}>
        <table className="adm__tabela">
          <thead>
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>Tela</th>
              <th style={{ width: 110 }}>Chegaram</th>
              <th style={{ width: 160 }}>Desistiram aqui</th>
              <th style={{ width: 200 }}>% do total</th>
            </tr>
          </thead>
          <tbody>
            {funil.etapas.map((e) => (
              <tr key={e.tela}>
                <td style={{ color: "var(--text3)" }}>{e.ordem}</td>
                <td style={{ fontWeight: 600 }}>{e.nome}</td>
                <td>{e.chegaram}</td>
                <td style={{ color: e.perdidosAqui > 0 ? "var(--orange)" : "var(--text3)", fontWeight: e.perdidosAqui > 0 ? 600 : 400 }}>
                  {e.perdidosAqui > 0 ? `${e.perdidosAqui} (${e.evasaoAqui})` : "—"}
                </td>
                <td>
                  <div style={{ position: "relative", background: "var(--elev)", borderRadius: 6, height: 20 }}>
                    <div style={{ position: "absolute", inset: "0 auto 0 0", width: e.doTotal, background: "var(--red)", borderRadius: 6 }} />
                    <span style={{ position: "relative", display: "block", lineHeight: "20px", paddingLeft: 8, fontSize: 11 }}>
                      {e.doTotal}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** O admin só ESCOLHE — nada é digitado à mão. */
function ModalDesvio({ origem, telas, vocab, onFechar, onSalvar }) {
  const [field, setField] = useState(vocab.campos[0]?.key || "");
  const [operator, setOperator] = useState("");
  const [value, setValue] = useState("");
  const [targetScreenId, setTargetScreenId] = useState("");

  const ops = useMemo(() => {
    const c = vocab.campos.find((x) => x.key === field);
    return c ? vocab.operadores.filter((o) => c.operators.includes(o.key)) : [];
  }, [field, vocab]);

  useEffect(() => {
    if (ops.length && !ops.some((o) => o.key === operator)) setOperator(ops[0].key);
  }, [ops, operator]);

  // Só pula PRA FRENTE — pra trás seria loop (o backend também recusa).
  const destinos = telas.filter((t) => t.visible && t.order > origem.order);

  return (
    <div className="adm__modalFundo" onClick={onFechar}>
      <div className="adm__modal" onClick={(e) => e.stopPropagation()}>
        <h3>Pular “{origem.name}”</h3>
        <p className="adm__modalHint">
          Você escolhe a condição — <strong>nada é digitado à mão</strong>. Se ela bater,
          a criança <strong>não vê esta tela</strong> e vai direto para a que você escolher.
        </p>

        <div className="adm__campo">
          <label>Se</label>
          <select className="adm__select" value={field} onChange={(e) => setField(e.target.value)}>
            {vocab.campos.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div className="adm__campo" style={{ flex: 1 }}>
            <label>Condição</label>
            <select className="adm__select" value={operator} onChange={(e) => setOperator(e.target.value)}>
              {ops.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="adm__campo" style={{ width: 110 }}>
            <label>Valor</label>
            <input className="adm__input" value={value} onChange={(e) => setValue(e.target.value)} placeholder="5" />
          </div>
        </div>

        <div className="adm__campo">
          <label>Então vai para</label>
          <select className="adm__select" value={targetScreenId} onChange={(e) => setTargetScreenId(e.target.value)}>
            <option value="">escolha a tela…</option>
            {destinos.map((t) => (
              <option key={t.id} value={t.id}>{t.order}. {t.name}</option>
            ))}
          </select>
        </div>

        {destinos.length === 0 && (
          <p style={{ fontSize: 12.5, color: "var(--orange)", lineHeight: 1.5 }}>
            Não há tela depois desta para onde ir. Um desvio só pode pular para frente.
          </p>
        )}

        {field && operator && value && targetScreenId && (
          <div
            style={{
              background: "var(--blueSoft)",
              borderLeft: "3px solid var(--blue)",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            → <strong>{origem.name}</strong> é pulada se{" "}
            <strong>{rotulo(vocab.campos, field)}</strong> {rotulo(vocab.operadores, operator)}{" "}
            <strong>{value}</strong>
          </div>
        )}

        <div className="adm__modalAcoes">
          <button className="adm__btnGhost" onClick={onFechar}>Cancelar</button>
          <button
            className="adm__btn"
            disabled={!field || !operator || !value || !targetScreenId}
            onClick={() => onSalvar({ screenId: origem.id, field, operator, value, targetScreenId })}
          >
            Salvar desvio
          </button>
        </div>
      </div>
    </div>
  );
}
